import { useEffect, useRef, useCallback } from "react";

/** useNotificationSound — plays a short ding when called.
 * Uses Web Audio API so no asset bundling needed.
 */
export function useNotificationSound() {
  const ctxRef = useRef(null);

  const init = useCallback(() => {
    if (!ctxRef.current && typeof window !== "undefined") {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) ctxRef.current = new Ctx();
      } catch (e) { /* ignore */ }
    }
  }, []);

  const ding = useCallback(() => {
    init();
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const playTone = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(0.25, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(now + start); o.stop(now + start + dur + 0.05);
    };
    playTone(880, 0, 0.18);
    playTone(660, 0.20, 0.22);
  }, [init]);

  useEffect(() => {
    // Prime audio on first user interaction (browsers block autoplay)
    const prime = () => { init(); window.removeEventListener("click", prime); };
    window.addEventListener("click", prime);
    return () => window.removeEventListener("click", prime);
  }, [init]);

  return ding;
}

/** useNewItemDetector — calls onNew(items) only for items that were created
 * AFTER the component mounted. This avoids the false-positive ding/toast
 * that fires every time a partner returns to the Inbox page (where the
 * existing booking list "appears" to be new). */
export function useNewItemDetector(items, onNew) {
  const seen = useRef(new Set());
  const mountedAt = useRef(Date.now());
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;
  useEffect(() => {
    if (!Array.isArray(items)) return;
    const additions = items.filter((x) => {
      if (seen.current.has(x.id)) return false;
      if (!x.created_at) return false;
      const t = new Date(x.created_at).getTime();
      // Only count items that landed AFTER we mounted (plus a small buffer
      // to absorb minor clock skew between client and server).
      return Number.isFinite(t) && t >= mountedAt.current - 2000;
    });
    if (additions.length > 0) onNewRef.current(additions);
    seen.current = new Set(items.map((x) => x.id));
  }, [items]);
}
